import { useQuery } from "@tanstack/react-query";
import { useIdentity } from "../../auth/useIdentity";
import { isApplicantOnly, isPlatformStaff, isSponsorOnly, isStaffOnly } from "../../components/nav";
import { listProviders, providerName, type Provider } from "../../api/providers";

export interface ProviderView extends Provider {
  name: string;
  /** Direct child of the namespace the console is acting on right now. */
  direct: boolean;
}

/**
 * The providers of the signed-in operator, asked ONCE for the token's own
 * namespace (every descendant comes back), then read two ways: the whole
 * tree for the header switcher, and the direct children of the CURRENT
 * namespace for the sidebar and the Welcome cards -- so inside Shedune the
 * list is Shedune's own providers, not Lotus's.
 */
export function useProviders() {
  const identity = useIdentity();
  const root = identity.rootNamespace;
  // Staff hold no operatorproviders:read: the call would only 403 (2026-10-18).
  // Platform staff hold no operatorproviders:read either (radaccess v1.154).
  const enabled = Boolean(identity.token && root) && !isSponsorOnly(identity.roles) && !isApplicantOnly(identity.roles) && !isStaffOnly(identity.roles) && !isPlatformStaff(identity.roles);
  const q = useQuery({
    queryKey: ["providers", root],
    enabled,
    queryFn: ({ signal }) => listProviders({ token: identity.token, namespace: root }, root!, signal),
    staleTime: 5 * 60_000,
  });
  const current = identity.namespace ?? "";
  const all: ProviderView[] = (q.data?.providers ?? []).map((p) => ({
    ...p,
    name: providerName(p),
    direct: p.namespace.startsWith(current + ".") && !p.namespace.slice(current.length + 1).includes("."),
  }));
  return {
    all,
    direct: all.filter((p) => p.direct),
    isLoading: enabled && q.isLoading,
    error: q.error,
    /** The provider the console is inside, with its name, or null. */
    inside: identity.provider ? (all.find((p) => p.namespace === identity.provider) ?? { namespace: identity.provider, legalname: "", active: true, depth: 0, name: providerName({ namespace: identity.provider, legalname: "" }), direct: false }) : null,
  };
}
